<template>
    <v-card>
        <v-card-title class="text-h5">Timer</v-card-title>

        <v-card-text>
            <v-row v-if="error">
                <v-col>
                    <v-alert color="red" icon="mdi-alert-circle-outline">
                        {{ error }}
                    </v-alert>
                </v-col>
            </v-row>

            <v-row align="center" class="mx-0 mt-2">
                <v-col class="py-0">
                    <v-radio-group v-model="contrast" label="Contrast (leave blank for colorful)">
                        <v-radio label="Dark (Black background)" value="dark" />
                        <v-radio label="Light (White background)" value="light" />
                    </v-radio-group>
                </v-col>
            </v-row>

            <v-row>
                <v-col class="py-0">
                    <v-checkbox v-model="hasStart" label="Start (optional)" />
                </v-col>

                <v-col> <div class="text-body-1 mt-2">End</div> </v-col>
            </v-row>

            <v-row>
                <v-col class="py-0">
                    <v-tabs v-model="startTab">
                        <v-tab href="#tab-datetime">Date/Time</v-tab>
                        <v-tab href="#tab-from-now">From Now</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="startTab">
                        <v-tab-item value="tab-datetime">
                            <v-time-picker
                                v-model="formComponents.start.time"
                                ampm-in-title
                                format="ampm"
                                :disabled="!hasStart"
                            />
                        </v-tab-item>

                        <v-tab-item value="tab-from-now">
                            <v-container>
                                <v-row>
                                    <v-col>
                                        <v-text-field
                                            v-model.number="formComponents.start.hours"
                                            :rules="hoursRules"
                                            :disabled="!hasStart"
                                            type="number"
                                            step="1"
                                            min="0"
                                            label="Hours"
                                            required
                                        />
                                    </v-col>
                                    <v-col>
                                        <v-text-field
                                            v-model.number="formComponents.start.minutes"
                                            :rules="minutesRules"
                                            :disabled="!hasStart"
                                            type="number"
                                            step="1"
                                            min="0"
                                            max="59"
                                            label="Minutes"
                                            required
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-tab-item>
                    </v-tabs-items>
                </v-col>
                <v-col class="py-0">
                    <v-tabs v-model="endTab">
                        <v-tab href="#tab-datetime">Date/Time</v-tab>
                        <v-tab href="#tab-duration">Duration</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="endTab">
                        <v-tab-item value="tab-datetime">
                            <v-time-picker v-model="formComponents.end.time" ampm-in-title format="ampm" />
                        </v-tab-item>

                        <v-tab-item value="tab-duration">
                            <v-container>
                                <v-row>
                                    <v-col>
                                        <v-text-field
                                            v-model.number="formComponents.end.hours"
                                            :rules="hoursRules"
                                            type="number"
                                            step="1"
                                            min="0"
                                            label="Hours"
                                            required
                                        />
                                    </v-col>
                                    <v-col>
                                        <v-text-field
                                            v-model.number="formComponents.end.minutes"
                                            :rules="minutesRules"
                                            type="number"
                                            step="1"
                                            min="0"
                                            max="59"
                                            label="Minutes"
                                            required
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-tab-item>
                    </v-tabs-items>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-alert color="info" icon="mdi-clock">
                        <div v-if="hasStart">
                            The timer will start at {{ startsAt.format('h:mm A') }} and end at
                            {{ endsAt.format('h:mm A') }} (lasting {{ sumMinutes }} minutes).
                        </div>
                        <div v-else>
                            The timer will end at {{ endsAt.format('h:mm A') }} (lasting {{ sumMinutes }} minutes).
                        </div>
                    </v-alert>
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-actions>
            <v-spacer />
            <v-btn :color="isPresenting ? 'default' : 'primary'" :disabled="sumMinutes < 0" @click="present">
                <span v-if="isPresenting"> New Timer </span>
                <span v-else> Present </span>
            </v-btn>
            <v-btn
                v-if="isPresenting"
                color="primary"
                :disabled="sumMinutes < 0"
                :loading="updatingTimer"
                @click="updateTimer"
            >
                Update Times
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import backgroundColors from '@/background-colors.js';

export default {
    name: 'TimerModal',

    data: () => ({
        startTab: 'tab-datetime',
        endTab: 'tab-datetime',
        timerId: null,

        error: null,
        updatingTimer: false,
        contrast: null,

        isPresenting: false,

        hasStart: true,

        formComponents: {
            start: {
                time: null,
                hours: null,
                minutes: null,
            },

            end: {
                time: null,
                hours: null,
                minutes: null,
            },
        },

        hoursRules: [],
        minutesRules: [],
    }),

    computed: {
        startsAt() {
            if (!this.hasStart) {
                return this.$moment();
            }

            if (this.startTab === 'tab-datetime') {
                return this.$moment(this.formComponents.start.time, 'HH:mm');
            }

            return this.$moment()
                .add(this.formComponents.start.hours, 'hours')
                .add(this.formComponents.start.minutes, 'minutes')
                .startOf('minute');
        },

        endsAt() {
            if (this.endTab === 'tab-datetime') {
                return this.$moment(this.formComponents.end.time, 'HH:mm');
            }

            const startTime = this.startsAt.clone() || this.$moment();

            return startTime
                .add(this.formComponents.end.hours, 'hours')
                .add(this.formComponents.end.minutes, 'minutes')
                .startOf('minute');
        },

        sumMinutes() {
            return this.endsAt.diff(this.startsAt, 'minutes');
        },
    },

    created() {
        this.formComponents.start.time = this.$moment().add(1, 'hour').startOf('hour').format('HH:mm');
        this.formComponents.end.time = this.$moment().add(1, 'hour').startOf('hour').add(55, 'minutes').format('HH:mm');

        this.timerId = Math.random().toString(36).substring(2, 6);
    },

    methods: {
        present() {
            const backgroundType = this.contrast === null ? 'gradient' : 'color';

            const dark = this.contrast !== 'light' ? 'true' : 'false';

            let url = `/timer?dark=${dark}&background_type=${backgroundType}`;

            url += `&timerId=${this.timerId}`;

            if (this.hasStart) {
                url += `&starts_at=${this.startsAt.format('YYYY-MM-DD%20HH:mm:ss')}`;
            }

            url += `&ends_at=${this.endsAt.format('YYYY-MM-DD%20HH:mm:ss')}`;

            if (this.contrast === 'dark') {
                url += `&background_colors[]=${encodeURIComponent('#000000,#000000')}`;
                url += '&color=ffffff';
            } else if (this.contrast === 'light') {
                url += `&background_colors[]=${encodeURIComponent('#ffffff,#ffffff')}`;
                url += '&color=000000';
            } else {
                backgroundColors.forEach(([startColor, endColor]) => {
                    url += `&background_colors[]=${encodeURIComponent(`${startColor},${endColor}`)}`;
                });
            }

            this.isPresenting = true;

            window.open(url, '_blank');
        },

        async updateTimer() {
            this.error = null;
            this.updatingTimer = true;

            try {
                await axios.put(`/api/timers/${this.timerId}`, {
                    starts_at: this.startsAt.format('YYYY-MM-DD HH:mm:ss'),
                    ends_at: this.endsAt.format('YYYY-MM-DD HH:mm:ss'),
                });
            } catch (e) {
                this.error = 'There was an error updating the timer. Please try again.';
            } finally {
                this.updatingTimer = false;
            }
        },
    },
};
</script>
